import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  HierarchyBrowserApiService,
  HierarchyNode,
  UserOption,
  OrgUnitType,
} from '../../services/hierarchy-browser-api.service';

export interface BrowserNode {
  id: number;
  name: string;
  displayName: string;
  ownerId: number | null;
  ownerName: string;
  costCenterName: string;
  costCenterCode: string;
  orgUnitTypeId: number | null;
  orgUnitType: string;
  description: string;
  expanded: boolean;
  children: BrowserNode[];
  level: 'portfolio' | 'subportfolio' | 'program';
}

function mapNode(raw: HierarchyNode, level: BrowserNode['level']): BrowserNode {
  const childLevel: BrowserNode['level'] =
    level === 'portfolio' ? 'subportfolio' : 'program';
  return {
    id: raw.id,
    name: raw.name,
    displayName: raw.display_name || raw.name,
    ownerId: raw.owner?.id ?? null,
    ownerName: raw.owner
      ? `${raw.owner.first_name} ${raw.owner.last_name}`.trim()
      : '',
    costCenterName: raw.cost_center_name || '',
    costCenterCode: raw.cost_center_code || '',
    orgUnitTypeId: (raw.org_unit_type && !isNaN(Number(raw.org_unit_type)))
      ? Number(raw.org_unit_type) : null,
    orgUnitType: raw.org_unit_type ? String(raw.org_unit_type) : '',
    description: raw.description || '',
    expanded: false,
    level,
    children: (raw.children || []).map(c => mapNode(c, childLevel)),
  };
}

@Component({
  selector: 'app-hierarchy-browser',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatAutocompleteModule],
  templateUrl: './hierarchy-browser.component.html',
  styleUrls: ['./hierarchy-browser.component.scss'],
})
export class HierarchyBrowserComponent implements OnInit {
  private api = inject(HierarchyBrowserApiService);

  // ── Tree data ─────────────────────────────────────────────────────────────
  portfolios: BrowserNode[] = [];
  loading = true;
  error = '';
  searchQuery = '';

  // ── Modal state ───────────────────────────────────────────────────────────
  modalType: 'portfolio' | 'subportfolio' | 'program' | null = null;
  modalParentPort: BrowserNode | null = null;
  modalParentSub: BrowserNode | null = null;
  isEditMode = false;
  editingNode: BrowserNode | null = null;

  // form fields
  formName = '';
  formOwnerId: number | '' = '';
  formCostCenterName = '';
  formCostCenterCode = '';
  formOrgUnitTypeId: number | '' = '';
  formDescription = '';
  ownerSearch = '';

  // dropdown data
  users: UserOption[] = [];
  orgUnitTypes: OrgUnitType[] = [];
  usersLoading = false;
  orgTypesLoading = false;

  // submit
  submitting = false;
  submitError = '';
  successMsg = '';

  // ── Init ──────────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    try {
      const data = await this.api.getHierarchy();
      this.portfolios = data.map(p => mapNode(p, 'portfolio'));
    } catch (err: any) {
      this.error = err?.error?.detail || err?.message || 'Failed to load hierarchy.';
    } finally {
      this.loading = false;
    }
  }

  // ── Tree helpers ──────────────────────────────────────────────────────────
  toggle(node: BrowserNode): void { node.expanded = !node.expanded; }

  expandAll(): void {
    const expand = (nodes: BrowserNode[]) =>
      nodes.forEach(n => { n.expanded = true; expand(n.children); });
    expand(this.portfolios);
  }

  collapseAll(): void {
    const collapse = (nodes: BrowserNode[]) =>
      nodes.forEach(n => { n.expanded = false; collapse(n.children); });
    collapse(this.portfolios);
  }

  get filteredPortfolios(): BrowserNode[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.portfolios;
    const filterNode = (node: BrowserNode): BrowserNode | null => {
      const filteredChildren = node.children.map(filterNode).filter((c): c is BrowserNode => c !== null);
      const selfMatch = node.name.toLowerCase().includes(q) || node.displayName.toLowerCase().includes(q);
      return selfMatch || filteredChildren.length
        ? { ...node, children: selfMatch ? node.children : filteredChildren, expanded: true }
        : null;
    };
    return this.portfolios.map(filterNode).filter((p): p is BrowserNode => p !== null);
  }

  get counts() {
    const subs  = this.portfolios.reduce((a, p) => a + p.children.length, 0);
    const progs = this.portfolios.reduce((a, p) => a + p.children.reduce((b, sp) => b + sp.children.length, 0), 0);
    return { portfolios: this.portfolios.length, subportfolios: subs, programs: progs };
  }

  // ── Owner autocomplete ────────────────────────────────────────────────────
  get filteredUsers(): UserOption[] {
    const q = this.ownerSearch.trim().toLowerCase();
    if (!q) return this.users;
    return this.users.filter(u =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q)
    );
  }

  ownerDisplayFn = (id: number | ''): string => {
    if (!id) return '';
    const u = this.users.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : '';
  };

  onOwnerSelected(userId: number): void {
    this.formOwnerId = userId;
    const u = this.users.find(u => u.id === userId);
    this.ownerSearch = u ? `${u.first_name} ${u.last_name}` : '';
  }

  onOwnerInputChange(): void {
    // If user clears the field, reset the selection
    if (!this.ownerSearch.trim()) this.formOwnerId = '';
  }

  // ── Modal open ────────────────────────────────────────────────────────────
  onAddPortfolio(): void {
    this.openModal('portfolio', null, null);
    this.loadUsers();
  }

  onAddSubPortfolio(port: BrowserNode): void {
    this.openModal('subportfolio', port, null);
    this.loadUsers();
    this.loadOrgUnitTypes();
  }

  onAddProgram(sub: BrowserNode): void {
    this.openModal('program', null, sub);
  }

  private openModal(
    type: 'portfolio' | 'subportfolio' | 'program',
    port: BrowserNode | null,
    sub: BrowserNode | null,
    editNode: BrowserNode | null = null
  ): void {
    this.modalType = type;
    this.modalParentPort = port;
    this.modalParentSub = sub;
    this.isEditMode = !!editNode;
    this.editingNode = editNode;
    // Pre-fill if editing, otherwise blank
    this.formName           = editNode?.displayName || '';
    this.formOwnerId        = editNode?.ownerId ?? '';
    this.formCostCenterName = editNode?.costCenterName || '';
    this.formCostCenterCode = editNode?.costCenterCode || '';
    this.formOrgUnitTypeId  = editNode?.orgUnitTypeId ?? '';
    this.formDescription    = editNode?.description || '';
    this.ownerSearch        = editNode?.ownerName || '';
    this.submitError = '';
    this.successMsg = '';
  }

  closeModal(): void {
    this.modalType = null;
    this.isEditMode = false;
    this.editingNode = null;
    this.ownerSearch = '';
    this.formOwnerId = '';
    this.submitError = '';
    this.successMsg = '';
  }

  // ── Edit handlers ─────────────────────────────────────────────────────────
  onEditPortfolio(port: BrowserNode): void {
    this.openModal('portfolio', null, null, port);
    this.loadUsers();
  }

  onEditSubPortfolio(port: BrowserNode, sub: BrowserNode): void {
    this.openModal('subportfolio', port, null, sub);
    this.loadUsers();
    this.loadOrgUnitTypes();
  }

  onEditProgram(sub: BrowserNode, prog: BrowserNode): void {
    this.openModal('program', null, sub, prog);
  }

  get modalTitle(): string {
    const action = this.isEditMode ? 'Edit' : 'Add';
    return this.modalType === 'portfolio' ? `${action} Portfolio`
         : this.modalType === 'subportfolio' ? `${action} Subportfolio`
         : `${action} Program`;
  }

  // ── Dropdown loaders ──────────────────────────────────────────────────────
  private async loadUsers(): Promise<void> {
    if (this.users.length || this.usersLoading) return;
    this.usersLoading = true;
    try { this.users = await this.api.getUsers(); }
    catch { /* silently ignore — dropdown will be empty */ }
    finally { this.usersLoading = false; }
  }

  private async loadOrgUnitTypes(): Promise<void> {
    if (this.orgUnitTypes.length || this.orgTypesLoading) return;
    this.orgTypesLoading = true;
    try { this.orgUnitTypes = await this.api.getOrgUnitTypes(); }
    catch { /* silently ignore */ }
    finally { this.orgTypesLoading = false; }
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  async submitForm(): Promise<void> {
    this.submitError = '';
    this.successMsg = '';

    if (this.modalType === 'portfolio') {
      if (!this.formName.trim() || !this.formOwnerId) {
        this.submitError = 'Name and Owner are required.'; return;
      }
      this.submitting = true;
      try {
        const payload = { name: this.formName.trim(), display_name: this.formName.trim(), owner_id: this.formOwnerId, level: 'portfolio' };
        if (this.isEditMode) {
          const res = await this.api.updatePortfolioOrSub(this.editingNode!.id, payload);
          const node = this.portfolios.find(p => p.id === this.editingNode!.id);
          if (node) Object.assign(node, mapNode({ ...res, children: (node.children as any) }, 'portfolio'));
          this.successMsg = 'Portfolio updated successfully.';
        } else {
          const res = await this.api.createPortfolio(payload);
          this.portfolios.push(mapNode(res, 'portfolio'));
          this.successMsg = 'Portfolio created successfully.';
        }
        setTimeout(() => this.closeModal(), 800);
      } catch (err: any) {
        this.submitError = err?.error?.name?.[0] || err?.error?.detail || 'Something went wrong.';
      } finally { this.submitting = false; }
    }

    else if (this.modalType === 'subportfolio') {
      if (!this.formName.trim() || !this.formOwnerId) {
        this.submitError = 'Name and Owner are required.'; return;
      }
      this.submitting = true;
      try {
        const payload = { name: this.formName.trim(), display_name: this.formName.trim(), owner_id: this.formOwnerId, level: 'sub_portfolio', cost_center_name: this.formCostCenterName.trim(), cost_center_code: this.formCostCenterCode.trim(), org_unit_type: this.formOrgUnitTypeId || null };
        if (this.isEditMode) {
          const res = await this.api.updatePortfolioOrSub(this.editingNode!.id, payload);
          for (const port of this.portfolios) {
            const sub = port.children.find(s => s.id === this.editingNode!.id);
            if (sub) { Object.assign(sub, mapNode({ ...res, children: (sub.children as any) }, 'subportfolio')); break; }
          }
          this.successMsg = 'Subportfolio updated successfully.';
        } else {
          const res = await this.api.createSubPortfolio(this.modalParentPort!.id, payload);
          const origPort = this.portfolios.find(p => p.id === this.modalParentPort!.id);
          origPort?.children.push(mapNode(res, 'subportfolio'));
          this.successMsg = 'Subportfolio created successfully.';
        }
        setTimeout(() => this.closeModal(), 800);
      } catch (err: any) {
        this.submitError = err?.error?.detail || err?.error?.name?.[0] || 'Something went wrong.';
      } finally { this.submitting = false; }
    }

    else if (this.modalType === 'program') {
      if (!this.formName.trim()) {
        this.submitError = 'Program Name is required.'; return;
      }
      this.submitting = true;
      try {
        const payload = { name: this.formName.trim(), cost_center_name: this.formCostCenterName.trim(), cost_center_code: this.formCostCenterCode.trim(), description: this.formDescription.trim() };
        if (this.isEditMode) {
          const res = await this.api.updateProgram(this.editingNode!.id, payload);
          for (const port of this.portfolios) {
            for (const sub of port.children) {
              const prog = sub.children.find(p => p.id === this.editingNode!.id);
              if (prog) { Object.assign(prog, mapNode({ ...res, children: [] }, 'program')); break; }
            }
          }
          this.successMsg = 'Program updated successfully.';
        } else {
          const res = await this.api.createProgram(this.modalParentSub!.id, payload);
          for (const port of this.portfolios) {
            const sub = port.children.find(s => s.id === this.modalParentSub!.id);
            if (sub) { sub.children.push(mapNode(res, 'program')); break; }
          }
          this.successMsg = 'Program created successfully.';
        }
        setTimeout(() => this.closeModal(), 800);
      } catch (err: any) {
        this.submitError = err?.error?.detail || err?.error?.name?.[0] || 'Something went wrong.';
      } finally { this.submitting = false; }
    }
  }
}
